import React, { useState } from 'react';
import { Search, Loader2, ExternalLink, Eye, Heart, MessageCircle, Share2, Link2, Music } from 'lucide-react';
import toast from 'react-hot-toast';

const formatNum = (n) => {
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(0) + 'K';
  return String(n ?? 0);
};

export default function SocialProfile() {
  const [handle, setHandle] = useState('');
  const [platform] = useState('tiktok');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matches, setMatches] = useState(null);

  const fetchProfile = async () => {
    const h = handle.replace(/^@/, '').trim();
    if (!h) {
      toast.error('Enter a handle');
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const res = await fetch(
        `/api/social-profile?handle=${encodeURIComponent(h)}&platform=${platform}`,
        { credentials: 'include' }
      );
      const json = await res.json();
      if (!res.ok) {
        toast.error(json.error || 'Failed to fetch');
        return;
      }
      if (!json.success) {
        toast.error(json.error || 'No data');
        return;
      }
      setData(json);
      setMatches(null);
      toast.success(`Loaded ${json.videos?.length ?? 0} videos`);
    } catch (e) {
      toast.error(e.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const runMatch = async () => {
    if (!data?.videos?.length) {
      toast.error('Fetch videos first');
      return;
    }
    setMatchLoading(true);
    setMatches(null);
    try {
      const res = await fetch('/api/social-profile/match', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          videos: data.videos.map((v) => ({
            rank: v.rank,
            desc: v.desc,
            url: v.url,
            postedAt: v.postedAt,
            impression: v.impression,
            likes: v.likes,
            comments: v.comments,
            shares: v.shares,
            hashtags: v.hashtags,
            musicName: v.musicName,
            musicAuthor: v.musicAuthor
          }))
        })
      });
      const json = await res.json();
      if (!res.ok) {
        toast.error(json.error || 'Match failed');
        return;
      }
      if (json.success && json.matches) {
        setMatches(json.matches);
        toast.success('Matched to trends');
      }
    } catch (e) {
      toast.error(e.message || 'Request failed');
    } finally {
      setMatchLoading(false);
    }
  };

  const matchByRank = matches && matches.length ? Object.fromEntries(matches.map((m) => [m.videoRank, m])) : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950 px-4 py-8">
      <div className="max-w-[1600px] mx-auto w-full">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Social Profile (hidden)
        </h1>
        <p className="text-sm text-gray-600 dark:text-zinc-400 mb-6">
          Fetch latest 10 videos by handle with engagement data, ranked by impression. TikTok only for now.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <select
            value={platform}
            disabled
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white text-sm"
          >
            <option value="tiktok">TikTok</option>
          </select>
          <input
            type="text"
            placeholder="TikTok handle (e.g. picklematchapp)"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchProfile()}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-gray-900 dark:text-white placeholder-gray-500 min-w-[220px]"
          />
          <button
            onClick={fetchProfile}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Fetch
          </button>
        </div>

        {data && (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-4">
              <div className="p-4 rounded-lg bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700">
                <span className="text-sm text-gray-600 dark:text-zinc-400">Total impression (sum of 10): </span>
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatNum(data.totalImpression)}
                </span>
              </div>
              <button
                type="button"
                onClick={runMatch}
                disabled={matchLoading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {matchLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Link2 className="h-4 w-4" />
                )}
                Match to trends
              </button>
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-900">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-0">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800">
                      <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-zinc-300">Rank</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-zinc-300">Video</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-zinc-300 min-w-[280px]">Caption & hashtags</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-zinc-300 min-w-[140px]">
                        <span className="inline-flex items-center gap-1"><Music className="h-4 w-4" /> Music</span>
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-zinc-300">Posted</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-zinc-300">
                        <span className="inline-flex items-center gap-1"><Eye className="h-4 w-4" /> Impressions</span>
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-zinc-300">
                        <span className="inline-flex items-center gap-1"><Heart className="h-4 w-4" /> Likes</span>
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-zinc-300">
                        <span className="inline-flex items-center gap-1"><MessageCircle className="h-4 w-4" /> Comments</span>
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-zinc-300">
                        <span className="inline-flex items-center gap-1"><Share2 className="h-4 w-4" /> Shares</span>
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-zinc-300">
                        Match score
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-zinc-300">
                        <span className="inline-flex items-center gap-1"><Link2 className="h-4 w-4" /> Matched trend</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.videos || []).map((row) => {
                      const m = matchByRank ? matchByRank[row.rank] : null;
                      return (
                        <tr
                          key={row.rank}
                          className="border-b border-gray-100 dark:border-zinc-800 hover:bg-gray-50 dark:hover:bg-zinc-800/50"
                        >
                          <td className="py-3 px-4 text-gray-900 dark:text-white font-medium">{row.rank}</td>
                          <td className="py-3 px-4">
                            {row.url ? (
                              <a
                                href={row.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-purple-600 dark:text-purple-400 hover:underline"
                              >
                                Link <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-gray-700 dark:text-zinc-300 min-w-[280px] max-w-[420px] align-top">
                            <div className="whitespace-pre-wrap break-words text-sm">
                              {row.desc || '—'}
                            </div>
                            {Array.isArray(row.hashtags) && row.hashtags.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {row.hashtags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="inline-flex items-center px-2 py-0.5 rounded-md bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-medium"
                                  >
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-gray-600 dark:text-zinc-400 align-top min-w-[140px] max-w-[200px]">
                            {row.musicName || row.musicAuthor ? (
                              <div className="text-xs">
                                {row.musicName && <div className="font-medium text-gray-800 dark:text-zinc-200 truncate" title={row.musicName}>{row.musicName}</div>}
                                {row.musicAuthor && <div className="text-gray-500 dark:text-zinc-500 truncate" title={row.musicAuthor}>{row.musicAuthor}</div>}
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-gray-600 dark:text-zinc-400">
                            {row.postedAt ? new Date(row.postedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </td>
                          <td className="py-3 px-4 text-right text-gray-900 dark:text-white">{formatNum(row.impression)}</td>
                          <td className="py-3 px-4 text-right text-gray-700 dark:text-zinc-300">{formatNum(row.likes)}</td>
                          <td className="py-3 px-4 text-right text-gray-700 dark:text-zinc-300">{formatNum(row.comments)}</td>
                          <td className="py-3 px-4 text-right text-gray-700 dark:text-zinc-300">{formatNum(row.shares)}</td>
                          <td className="py-3 px-4 text-right text-gray-700 dark:text-zinc-300 align-top">
                            {matchByRank && m?.matchScore != null ? (
                              <span
                                className={`font-medium tabular-nums ${
                                  m.matchScore >= 0.8
                                    ? 'text-emerald-600 dark:text-emerald-400'
                                    : m.matchScore >= 0.5
                                    ? 'text-amber-600 dark:text-amber-400'
                                    : 'text-gray-500 dark:text-zinc-500'
                                }`}
                                title="0 = least match, 1 = best match"
                              >
                                {(m.matchScore * 100).toFixed(0)}%
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-gray-700 dark:text-zinc-300 align-top">
                            {m?.trendName && m?.trendId ? (
                              <a
                                href={`/share/trend/${m.trendId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-600 dark:text-emerald-400 font-medium hover:underline"
                                title="Open trend details in new tab"
                              >
                                {m.trendName}
                              </a>
                            ) : matchByRank ? (
                              <span className="text-gray-400">—</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
